import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

type Variant = "card" | "split" | "inline";

interface FinalCTAProps {
  title: string;
  description?: string;
  primaryLabel: string;
  primaryTo?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  note?: ReactNode;
  variant?: Variant;
}

/**
 * Reusable closing CTA with three layout variants.
 * Outer is always white → clear break before the footer.
 *
 * - "card"   (A): forest rounded card, centered, compact paddings
 * - "split"  (B): horizontal split – headline left, buttons right, forest strip
 * - "inline" (C): minimal inline forest band with thin top border, very tight
 */
const FinalCTA = ({
  title,
  description,
  primaryLabel,
  primaryTo = "/kontakt",
  primaryHref,
  secondaryLabel = "info@energieglueck.de",
  secondaryHref = "mailto:info@energieglueck.de",
  note,
  variant = "card",
}: FinalCTAProps) => {
  const PrimaryButton = (
    <Button asChild size="lg" className="btn-primary">
      {primaryHref ? (
        <a href={primaryHref}>
          {primaryLabel}
          <ArrowRight className="w-4 h-4 ml-2" />
        </a>
      ) : (
        <Link to={primaryTo}>
          {primaryLabel}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      )}
    </Button>
  );

  const SecondaryButton = secondaryLabel && (
    <Button
      asChild
      size="lg"
      variant="outline"
      className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white"
    >
      <a href={secondaryHref}>
        <Mail className="w-4 h-4 mr-2" />
        {secondaryLabel}
      </a>
    </Button>
  );

  // ---------- Variante A: Card kompakt ----------
  if (variant === "card") {
    return (
      <section className="py-10 md:py-14 bg-white">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl bg-ci-forest text-white px-6 py-10 md:px-14 md:py-12 shadow-xl">
            <div className="absolute top-0 inset-x-0 h-1 bg-primary" />
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-display text-2xl md:text-4xl font-extrabold tracking-tight leading-[1.1] mb-3">
                {title}
              </h2>
              {description && (
                <p className="text-base md:text-lg text-white/80 mb-6 leading-relaxed max-w-2xl mx-auto">
                  {description}
                </p>
              )}
              <div className="flex flex-wrap justify-center gap-3 mb-3">
                {PrimaryButton}
                {SecondaryButton}
              </div>
              {note && (
                <p className="inline-flex items-center gap-2 text-sm text-white/70">
                  <Check className="w-4 h-4 text-primary" />
                  {note}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---------- Variante B: Split horizontal ----------
  if (variant === "split") {
    return (
      <section className="py-10 md:py-14 bg-white">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-2xl bg-ci-forest text-white px-6 py-8 md:px-12 md:py-10 shadow-lg">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
            <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight leading-[1.15] mb-2">
                  {title}
                </h2>
                {description && (
                  <p className="text-base text-white/80 leading-relaxed max-w-xl">
                    {description}
                  </p>
                )}
                {note && (
                  <p className="mt-3 inline-flex items-center gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-primary" />
                    {note}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:items-stretch">
                {PrimaryButton}
                {SecondaryButton}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---------- Variante C: Inline minimal ----------
  return (
    <section className="bg-white">
      <div className="container-custom py-8 md:py-10">
        <div className="border-t border-ci-forest/15 pt-8 md:pt-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-2">
                Nächster Schritt
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-ci-forest leading-[1.15]">
                {title}
              </h2>
              {description && (
                <p className="mt-2 text-foreground/70 leading-relaxed">{description}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Button asChild size="lg" className="btn-primary">
                {primaryHref ? (
                  <a href={primaryHref}>
                    {primaryLabel}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                ) : (
                  <Link to={primaryTo}>
                    {primaryLabel}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                )}
              </Button>
              {secondaryLabel && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-ci-forest/20 text-ci-forest hover:bg-ci-forest/5"
                >
                  <a href={secondaryHref}>
                    <Mail className="w-4 h-4 mr-2" />
                    {secondaryLabel}
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
